import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ThemeIcon } from '@mantine/core'
import { Spotlight, SpotlightActionData, SpotlightActionGroupData } from '@mantine/spotlight'
import { TbSearch, TbCpu, TbTool } from 'react-icons/tb'

import { getAllVehiclesOptions } from '@/api/main/@tanstack/react-query.gen'
import { useAuthStore } from '@/stores/auth'
import { hasPermission } from '@/utils/permissions'
import { useRecentPagesStore } from '@/stores/recentPages'
import { useSearchableLinks } from '@/hooks/useSearchableLinks'
import { SearchableLink } from '@/components/Navigation.shared'
import { MAP_SNAPSHOTS_ENABLED } from '@/constants'

const ActionIcon = ({ icon: Icon }: { icon: SearchableLink['icon'] }) => (
  <ThemeIcon variant="light" size={30}>
    <Icon size={18} />
  </ThemeIcon>
)

export const CommandPalette = () => {
  const { t, i18n } = useTranslation('nav')
  const navigate = useNavigate()

  const userPermissions = useAuthStore(state => state.userPermissions)
  const searchableLinks = useSearchableLinks()
  const getTopLinks = useRecentPagesStore(state => state.getTopLinks)
  const visits = useRecentPagesStore(state => state.visits)

  const canHardware = hasPermission(userPermissions, 'vehicle_administration', 'write')
  const { data: vehicles } = useQuery({ ...getAllVehiclesOptions(), enabled: canHardware })

  const groups = useMemo<SpotlightActionGroupData[]>(() => {
    const linkToAction = (link: SearchableLink): SpotlightActionData => ({
      id: link.link,
      label: t(link.labelKey as Parameters<typeof t>[0]),
      keywords: link.link,
      leftSection: <ActionIcon icon={link.icon} />,
      onClick: () => navigate({ to: link.link }),
    })

    const result: SpotlightActionGroupData[] = []

    // 1. Most used
    const topLinks = getTopLinks(searchableLinks, 5)
    if (topLinks.length > 0) {
      result.push({
        group: t('palette.mostUsed'),
        actions: topLinks.map(linkToAction),
      })
    }

    // 2. Navigation sections, grouped by parent tab (or section for top-level leaves)
    const navGroups = new Map<string, SpotlightActionData[]>()
    for (const link of searchableLinks) {
      const groupKey = link.groupKey ?? (link.section === 'users' ? 'users' : 'system')
      const label = t(groupKey as Parameters<typeof t>[0])
      const actions = navGroups.get(label) ?? []
      actions.push(linkToAction(link))
      navGroups.set(label, actions)
    }
    for (const [group, actions] of navGroups) {
      result.push({ group, actions })
    }

    // 2b. Control data tables — deep links that scroll to a specific table.
    const tableActions: SpotlightActionData[] = [
      {
        id: '/control-data#schemas',
        label: t('controlDataSchemas'),
        keywords: ['control-data', 'schemas', '/control-data#schemas'],
        leftSection: <ActionIcon icon={TbTool} />,
        onClick: () => navigate({ to: '/control-data', hash: 'schemas' }),
      },
    ]
    if (MAP_SNAPSHOTS_ENABLED) {
      tableActions.unshift({
        id: '/control-data#snapshots',
        label: t('controlDataSnapshots'),
        keywords: ['control-data', 'snapshots', 'map snapshots', '/control-data#snapshots'],
        leftSection: <ActionIcon icon={TbTool} />,
        onClick: () => navigate({ to: '/control-data', hash: 'snapshots' }),
      })
    }
    result.push({ group: t('controlDataEdit'), actions: tableActions })

    // 3. Hardware — dynamic per-vehicle (no sidebar entry), gated by vehicle_administration:write
    if (canHardware && vehicles && vehicles.length > 0) {
      result.push({
        group: t('hardware'),
        actions: vehicles.map(vehicle => ({
          id: `hardware-${vehicle.id}`,
          label: vehicle.id,
          description: t('hardware'),
          keywords: ['hardware', vehicle.id],
          leftSection: <ActionIcon icon={TbCpu} />,
          onClick: () => navigate({ to: `/hardware/${vehicle.id}/restarts` }),
        })),
      })
    }

    return result
    // i18n.language drives label re-translation on language switch; visits drives Most used.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchableLinks, vehicles, canHardware, visits, i18n.language])

  return (
    <Spotlight
      shortcut="mod + K"
      actions={groups}
      nothingFound={t('palette.noResults')}
      highlightQuery
      scrollAreaProps={{ type: 'auto', style: { maxHeight: '50vh' } }}
      searchProps={{
        placeholder: t('palette.searchPlaceholder'),
        leftSection: <TbSearch size={18} />,
      }}
    />
  )
}

export default CommandPalette
